/**
 * Tarati engine — bitboard move generation, evaluation and negamax search.
 *
 * This is a direct port of `strategy/engine/bitboard.py` and
 * `strategy/engine/ai.py`, and is kept in lockstep with them: structure,
 * naming and constants mirror the Python one on purpose so the two stay
 * comparable. `src/__tests__/aiParity.test.js` asserts both engines against a
 * shared golden fixture generated by `strategy/export_parity_fixture.py`.
 *
 * A position is four plain integers: `occ`, `white`, `rok` and `stm`. The 23
 * vertices fit in 23 bits, comfortably inside JavaScript's 32-bit bitwise
 * operators, so nothing is allocated per node beyond the move list.
 *
 * Colours are WHITE = 0 and BLACK = 1 so they can index tuples directly.
 * Move encoding: `(from << 5) | to`. A move with `from === to` is the
 * dead-piece promotion of patent §6.3.
 *
 * Four search properties are load-bearing — the previous version of this file
 * got all four wrong, which is why the difficulty ladder was meaningless:
 *
 *   1. Terminal scoring depends on whose turn it is, not on material. A player
 *      who cannot move has lost, however far ahead they are. The old code used
 *      `score < 0 ? +WIN : -WIN` — material sign as a proxy for the winner —
 *      which made the engine structurally unable to see "ahead but jammed",
 *      the characteristic loss in a game with forward-only movement.
 *   2. Transposition entries carry a bound flag and are reused only at exactly
 *      the depth they were searched to. See `search` for why.
 *   3. Root moves are searched with a full window. Narrowing alpha/beta across
 *      sibling root moves turns every root score after the first into a bound,
 *      which breaks both best-move selection and temperature sampling.
 *   4. Temperature is in centipieces. The old code softmaxed raw scores whose
 *      unit is ~100 per piece, so `exp(-100)` collapsed to a uniform
 *      tie-breaker rather than an exploration knob.
 *
 * Evaluation is in centipieces: 100 == one piece.
 */

import { gameBoard, applyMoveToBoard, ADJACENCY, EDGE_SET } from './GameBoard';

// -- Colours ---------------------------------------------------------------

const WHITE = 0;
const BLACK = 1;
const COLOR_NAME = ['WHITE', 'BLACK'];
const OPPONENT = { WHITE: 'BLACK', BLACK: 'WHITE' };

// -- Vertices --------------------------------------------------------------

const VERTICES = gameBoard.vertices;
const N_VERTICES = VERTICES.length; // 23
const FULL = (1 << N_VERTICES) - 1;

const INDEX = {};
VERTICES.forEach((v, i) => { INDEX[v] = i; });

const maskOf = (vertices) => {
  let m = 0;
  for (const v of vertices) m |= 1 << INDEX[v];
  return m;
};

// -- Rank / forward tables -------------------------------------------------
//
// RANK maps each vertex to its row index on the board, 0 at BLACK's home-base
// line (D3/D4) up to 10 at WHITE's (D1/D2). It replaces the old float test
// `POSITION_Y[from] - POSITION_Y[to] > 10` in the move validator with an
// explicit table — same rule, no geometry dependency. The values were derived
// once by clustering the render Y-coordinates with that same 10px deadband
// (every genuine row gap is >= 125px, so the clustering is unambiguous) and
// then frozen; all 84 directed edges reproduce the old rule exactly.
//
// The board has a 180° automorphism swapping the two home bases (A1→A1,
// Bi→B(i+3), Ci→C(i+6), D1↔D3, D2↔D4), under which RANK[m(v)] == MAX_RANK -
// RANK[v]. Building the forward rule and the evaluation on RANK therefore
// makes both provably colour-symmetric.

const RANK = {
  A1: 5,
  B1: 7, B2: 6, B3: 4, B4: 3, B5: 4, B6: 6,
  C1: 9, C2: 9, C3: 8, C4: 6, C5: 4, C6: 2,
  C7: 1, C8: 1, C9: 2, C10: 4, C11: 6, C12: 8,
  D1: 10, D2: 10, D3: 0, D4: 0,
};
const MAX_RANK = 10;

// FORWARD[colour][vertex] → neighbours that count as "forward" for a cob of
// that colour. WHITE advances toward rank 0, BLACK toward MAX_RANK.
const FORWARD = { WHITE: {}, BLACK: {} };
for (const v of VERTICES) {
  FORWARD.WHITE[v] = ADJACENCY[v].filter((n) => RANK[n] < RANK[v]);
  FORWARD.BLACK[v] = ADJACENCY[v].filter((n) => RANK[n] > RANK[v]);
}

const HOME_BASES = {
  WHITE: gameBoard.homeBases.white,
  BLACK: gameBoard.homeBases.black,
};

// Outermost home-base points where a non-upgraded cob becomes dead (patent §6.1)
const DEAD_POSITIONS = {
  WHITE: ['D3', 'D4'],
  BLACK: ['D1', 'D2'],
};

// -- Precomputed topology (bitmasks) ---------------------------------------

const ADJ = VERTICES.map((v) => maskOf(ADJACENCY[v]));
const FWD = [
  VERTICES.map((v) => maskOf(FORWARD.WHITE[v])),
  VERTICES.map((v) => maskOf(FORWARD.BLACK[v])),
];
const HOME = [maskOf(HOME_BASES.WHITE), maskOf(HOME_BASES.BLACK)];
const DEAD = [maskOf(DEAD_POSITIONS.WHITE), maskOf(DEAD_POSITIONS.BLACK)];
const RANK_OF = VERTICES.map((v) => RANK[v]);

// -- Bit helpers -----------------------------------------------------------
//
// Hot loops iterate set bits inline with `for (let m = x; m; m &= m - 1)` and
// `31 - Math.clz32(m & -m)` so they allocate nothing; `bitList` exists only for
// the cold conversion paths.

const lowestIndex = (m) => 31 - Math.clz32(m & -m);

function bitList(mask) {
  const out = [];
  for (let m = mask; m; m &= m - 1) out.push(lowestIndex(m));
  return out;
}

function popcount(x) {
  let v = x - ((x >> 1) & 0x55555555);
  v = (v & 0x33333333) + ((v >> 2) & 0x33333333);
  v = (v + (v >> 4)) & 0x0f0f0f0f;
  return Math.imul(v, 0x01010101) >>> 24;
}

// -- Zobrist hashing -------------------------------------------------------
//
// A fixed 32-bit table derived from an xorshift32 walk of a fixed seed, so the
// hash is stable across reloads and across app versions — the app stores these
// values in its repetition history. Values deliberately do NOT match the Python
// engine's: that side uses 64-bit keys, which JavaScript cannot hold in bitwise
// operators, and since the depth-exact table rule below makes both engines
// transposition-transparent, identical hashes are unnecessary for identical
// results.

const { ZOBRIST, ZOBRIST_SIDE } = (() => {
  let s = 0x0ddba11 >>> 0;
  const next = () => {
    s ^= (s << 13); s >>>= 0;
    s ^= (s >>> 17); s >>>= 0;
    s ^= (s << 5); s >>>= 0;
    return s;
  };
  const table = new Int32Array(N_VERTICES * 4); // vertex x {white,black} x {cob,rok}
  for (let i = 0; i < table.length; i += 1) table[i] = next() | 0;
  return { ZOBRIST: table, ZOBRIST_SIDE: next() | 0 };
})();

function zobrist(occ, white, rok, stm) {
  let h = stm ? ZOBRIST_SIDE : 0;
  for (let m = occ; m; m &= m - 1) {
    const i = lowestIndex(m);
    const colour = (white >> i) & 1 ? 0 : 1;
    const rank = (rok >> i) & 1;
    h ^= ZOBRIST[(i << 2) | (colour << 1) | rank];
  }
  return h >>> 0;
}

// -- Move generation -------------------------------------------------------

function genMoves(occ, white, rok, stm) {
  const own = stm === WHITE ? white : occ & ~white;
  const empty = FULL & ~occ;
  const free = own & (rok | HOME[stm]); // roks (§3.3) and cobs at home (§3.2)
  const forwardTable = FWD[stm];

  const moves = [];
  for (let m = free; m; m &= m - 1) {
    const i = lowestIndex(m);
    for (let t = ADJ[i] & empty; t; t &= t - 1) moves.push((i << 5) | lowestIndex(t));
  }
  for (let m = own & ~free; m; m &= m - 1) {
    const i = lowestIndex(m);
    for (let t = forwardTable[i] & empty; t; t &= t - 1) moves.push((i << 5) | lowestIndex(t));
  }
  if (moves.length > 0) return moves;

  // No normal move — a dead cob may be promoted so that it can move (§6.3).
  for (let m = own & ~rok & DEAD[stm]; m; m &= m - 1) {
    const i = lowestIndex(m);
    if (ADJ[i] & empty) moves.push((i << 5) | i);
  }
  return moves;
}

function hasMoves(occ, white, rok, stm) {
  const own = stm === WHITE ? white : occ & ~white;
  const empty = FULL & ~occ;
  const free = own & (rok | HOME[stm]);
  for (let m = free; m; m &= m - 1) {
    if (ADJ[lowestIndex(m)] & empty) return true;
  }
  const forwardTable = FWD[stm];
  for (let m = own & ~free; m; m &= m - 1) {
    if (forwardTable[lowestIndex(m)] & empty) return true;
  }
  for (let m = own & ~rok & DEAD[stm]; m; m &= m - 1) {
    if (ADJ[lowestIndex(m)] & empty) return true;
  }
  return false;
}

// `makeMove` writes its result into these module-level scalars rather than
// allocating a tuple per node. Callers must copy them into locals before
// recursing, because the next `makeMove` overwrites them.
let MM_OCC = 0;
let MM_WHITE = 0;
let MM_ROK = 0;
let MM_STM = 0;

/**
 * Apply `move`, leaving (occ, white, rok, stm) in MM_* with the turn toggled.
 *
 * Mirrors GameBoard.applyMoveToBoard exactly, including the fact that a
 * captured piece is never promoted: the promotion branch guarding struck pieces
 * in the dict engine is unreachable (its inner test contradicts its outer one),
 * and the rule it would implement is covered by §5.2 anyway.
 */
function makeMove(occ, white, rok, stm, move) {
  const src = move >> 5;
  const dst = move & 31;

  if (src === dst) { // dead-piece promotion in place (§6.3)
    MM_OCC = occ;
    MM_WHITE = white;
    MM_ROK = rok | (1 << src);
    MM_STM = stm ^ 1;
    return;
  }

  const srcBit = 1 << src;
  const dstBit = 1 << dst;
  const moverIsWhite = (white >> src) & 1;

  let nOcc = (occ ^ srcBit) | dstBit;
  let nWhite = moverIsWhite ? (white ^ srcBit) | dstBit : white;
  let nRok = rok;
  if ((rok >> src) & 1) nRok = (rok ^ srcBit) | dstBit;
  else if (HOME[stm ^ 1] & dstBit) nRok = rok | dstBit; // promotion on the far home base (§5.1)

  // Strike (§4): every enemy adjacent to the destination that the mover was not
  // already adjacent to before moving (§4.1, the pre-adjacency rule).
  const enemy = moverIsWhite ? nOcc & ~nWhite : nWhite;
  const victims = ADJ[dst] & ~ADJ[src] & enemy;
  if (victims) {
    if (moverIsWhite) nWhite |= victims;
    else nWhite &= ~victims;
  }

  // Sole remaining piece of a colour must be promoted (§6.4).
  const nBlack = nOcc & ~nWhite;
  if (popcount(nWhite) === 1 && !(nWhite & nRok)) nRok |= nWhite;
  if (popcount(nBlack) === 1 && !(nBlack & nRok)) nRok |= nBlack;

  MM_OCC = nOcc;
  MM_WHITE = nWhite;
  MM_ROK = nRok;
  MM_STM = stm ^ 1;
}

// -- Terminal detection ----------------------------------------------------

/** Colour that has lost, or -1. Patent §7.1. */
function terminalLoser(occ, white, rok, stm) {
  const black = occ & ~white;
  if (!black) return BLACK;
  if (!white) return WHITE;
  if (!hasMoves(occ, white, rok, stm)) return stm;
  return -1;
}

// -- Evaluation ------------------------------------------------------------

export const EVAL_WEIGHTS = {
  material: 100,
  rok: 60,
  centre_a1: 18,
  centre_b: 10,
  advancement: 4,
  mobility: 3,
  home_guard: 8,
  jammed_cob: -25,
};

// Per-square value tables, indexed [colour][vertex]. Folding every positional
// term into a lookup keyed by (colour, vertex, rank) turns evaluation into a
// sum over set bits. Because RANK is antisymmetric under the board's 180°
// automorphism and every vertex set used here maps onto its opposite, the
// tables satisfy COB_VALUE[WHITE][i] === COB_VALUE[BLACK][rotate(i)] — which is
// what makes the evaluation provably colour-symmetric.
const COB_VALUE = [[], []];
const ROK_VALUE = [[], []];
let MOBILITY_WEIGHT = 0;

function setWeights(weights = EVAL_WEIGHTS) {
  MOBILITY_WEIGHT = weights.mobility;
  for (const colour of [WHITE, BLACK]) {
    COB_VALUE[colour] = [];
    ROK_VALUE[colour] = [];
    for (let i = 0; i < N_VERTICES; i += 1) {
      const bit = 1 << i;
      let common = weights.material;
      if (i === INDEX.A1) common += weights.centre_a1;
      else if (VERTICES[i][0] === 'B') common += weights.centre_b;
      if (HOME[colour] & bit) common += weights.home_guard;

      const advance = colour === WHITE ? MAX_RANK - RANK_OF[i] : RANK_OF[i];
      let cobValue = common + weights.advancement * advance;
      if (DEAD[colour] & bit) cobValue += weights.jammed_cob;

      COB_VALUE[colour].push(cobValue);
      ROK_VALUE[colour].push(common + weights.rok);
    }
  }
}

setWeights();

function mobility(occ, white, rok, colour) {
  const own = colour === WHITE ? white : occ & ~white;
  const empty = FULL & ~occ;
  const free = own & (rok | HOME[colour]);
  const forwardTable = FWD[colour];
  let total = 0;
  for (let m = free; m; m &= m - 1) total += popcount(ADJ[lowestIndex(m)] & empty);
  for (let m = own & ~free; m; m &= m - 1) total += popcount(forwardTable[lowestIndex(m)] & empty);
  return total;
}

/** Static evaluation in centipieces — positive favours WHITE. */
function evaluate(occ, white, rok) {
  const black = occ & ~white;
  const whiteCob = COB_VALUE[WHITE];
  const whiteRok = ROK_VALUE[WHITE];
  const blackCob = COB_VALUE[BLACK];
  const blackRok = ROK_VALUE[BLACK];

  let score = 0;
  for (let m = white & ~rok; m; m &= m - 1) score += whiteCob[lowestIndex(m)];
  for (let m = white & rok; m; m &= m - 1) score += whiteRok[lowestIndex(m)];
  for (let m = black & ~rok; m; m &= m - 1) score -= blackCob[lowestIndex(m)];
  for (let m = black & rok; m; m &= m - 1) score -= blackRok[lowestIndex(m)];

  if (MOBILITY_WEIGHT) {
    score += MOBILITY_WEIGHT
      * (mobility(occ, white, rok, WHITE) - mobility(occ, white, rok, BLACK));
  }
  return score;
}

// -- Move ordering ---------------------------------------------------------

/** Static ordering heuristic — never applies the move. */
function orderScore(occ, white, rok, stm, move, ttMove) {
  if (move === ttMove) return 1000000;
  const src = move >> 5;
  const dst = move & 31;
  if (src === dst) return 5000;

  const enemy = stm === WHITE ? occ & ~white : white;
  const captures = popcount(ADJ[dst] & ~ADJ[src] & enemy & ~(1 << src));
  let score = captures * 1000;

  if (!((rok >> src) & 1) && (HOME[stm ^ 1] & (1 << dst))) score += 400;
  if (stm === WHITE) score += (RANK_OF[src] - RANK_OF[dst]) * 5;
  else score += (RANK_OF[dst] - RANK_OF[src]) * 5;
  return score;
}

/**
 * Sort `moves` in place by descending order score.
 *
 * Insertion sort: the lists are tiny, and it is stable, which matters because
 * ties among equally-ordered moves must resolve the same way here as they do
 * under Python's stable `list.sort(reverse=True)`.
 */
function orderMoves(moves, occ, white, rok, stm, ttMove) {
  const n = moves.length;
  const keys = new Array(n);
  for (let i = 0; i < n; i += 1) keys[i] = orderScore(occ, white, rok, stm, moves[i], ttMove);
  for (let i = 1; i < n; i += 1) {
    const move = moves[i];
    const key = keys[i];
    let j = i - 1;
    while (j >= 0 && keys[j] < key) {
      moves[j + 1] = moves[j];
      keys[j + 1] = keys[j];
      j -= 1;
    }
    moves[j + 1] = move;
    keys[j + 1] = key;
  }
}

// -- Conversion to and from the dict representation ------------------------

/** dict game-state -> [occ, white, rok, stm]. */
function fromState(state) {
  let occ = 0;
  let white = 0;
  let rok = 0;
  for (const [vertex, checker] of Object.entries(state.checkers)) {
    const bit = 1 << INDEX[vertex];
    occ |= bit;
    if (checker.color === 'WHITE') white |= bit;
    if (checker.isUpgraded) rok |= bit;
  }
  return [occ, white, rok, state.currentTurn === 'WHITE' ? WHITE : BLACK];
}

/** [occ, white, rok, stm] -> dict game-state. */
function toState(occ, white, rok, stm) {
  const checkers = {};
  for (const i of bitList(occ)) {
    checkers[VERTICES[i]] = {
      color: (white >> i) & 1 ? 'WHITE' : 'BLACK',
      isUpgraded: !!((rok >> i) & 1),
    };
  }
  return { checkers, currentTurn: COLOR_NAME[stm] };
}

const moveToVertices = (move) => ({ from: VERTICES[move >> 5], to: VERTICES[move & 31] });

// -- Search constants ------------------------------------------------------

const WINNING_SCORE = 1000000;
const MAX_SEARCH_PLY = 256;
const MATE_THRESHOLD = WINNING_SCORE - MAX_SEARCH_PLY;

// Softmax temperature for stochastic move choice, in centipieces. 25 == a
// quarter of a piece.
const DEFAULT_TEMPERATURE = 25;

// Transposition bound flags
const EXACT = 0;
const LOWER = 1;
const UPPER = 2;

/**
 * Thrown when the node or time budget is exhausted mid-search. Unwinding by
 * exception guarantees no partial result reaches the transposition table and no
 * truncated score is compared against a complete one — the root simply falls
 * back to the last fully completed iteration.
 */
const SEARCH_ABORTED = { searchAborted: true };

const now = () => (typeof performance !== 'undefined' && performance.now
  ? performance.now()
  : Date.now());

/**
 * Depth-keyed value cache plus a depth-agnostic move-ordering hint.
 *
 * `values` is keyed by (position, depth) because a value is only valid at the
 * depth it was searched to. `moves` is keyed by position alone: a best move
 * found at any depth is worth trying first, and using it cannot affect
 * correctness because it only reorders the move list. That hint is what makes
 * iterative deepening pay for itself.
 *
 * Entries carry the position they were computed for and probes verify it. The
 * 32-bit Zobrist key is small enough that birthday collisions are plausible
 * inside a single deep search, and an unverified collision would let one
 * position's bound answer for another. Verifying makes the JS table behave like
 * the Python engine's effectively collision-free 64-bit one.
 */
class TranspositionTable {
  constructor() {
    this.values = new Map();
    this.moves = new Map();
  }
}

const valueKey = (hash, depth) => hash * 512 + depth; // hash < 2^32, depth <= 256

function checkBudget(ctx) {
  if (ctx === null) return;
  ctx.nodes += 1;
  if (ctx.maxNodes !== null && ctx.nodes >= ctx.maxNodes) throw SEARCH_ABORTED;
  // now() is comparatively expensive; sample it rather than call it every node.
  if (ctx.deadline !== null && (ctx.nodes & 255) === 0 && now() >= ctx.deadline) {
    throw SEARCH_ABORTED;
  }
}

/** Re-base a mate score to be relative to this node before storing. */
function ttStoreValue(value, ply) {
  if (value > MATE_THRESHOLD) return value + ply;
  if (value < -MATE_THRESHOLD) return value - ply;
  return value;
}

/** Re-base a stored mate score to be relative to the current root. */
function ttLoadValue(value, ply) {
  if (value > MATE_THRESHOLD) return value - ply;
  if (value < -MATE_THRESHOLD) return value + ply;
  return value;
}

/** Negate without producing -0, which Jest's equality treats as distinct. */
const neg = (value) => (value === 0 ? 0 : -value);

/**
 * Negamax with alpha-beta on bitboards. Returns a side-to-move score.
 *
 * Table entries are reused only at **exactly** the depth they were searched to.
 * Chess engines conventionally accept any entry of greater-or-equal depth,
 * treating the deeper value as strictly better, but that makes the result
 * depend on search history: in fixed-depth minimax the depth-8 value of a
 * position is a different quantity from its depth-5 value, so a depth-8 bound
 * licenses no conclusion about a depth-5 window. Reusing it anyway lets a bound
 * masquerade as an exact value and the corruption propagates upward — which is
 * how a root move here came back scoring -309 against a true -530.
 *
 * Matching on exact depth keeps every genuine same-depth transposition (the
 * bulk of the benefit) while making the search a pure function of the position.
 */
function search(occ, white, rok, stm, depth, alpha, beta, ply, tt, ctx) {
  checkBudget(ctx);

  const alphaOrig = alpha;
  let ttMove = -1;
  let key = 0;
  let currentAlpha = alpha;
  let currentBeta = beta;

  if (tt !== null) {
    key = zobrist(occ, white, rok, stm);
    const hint = tt.moves.get(key); // ordering hint, valid at any depth
    if (hint !== undefined
      && hint.occ === occ && hint.white === white && hint.rok === rok && hint.stm === stm) {
      ttMove = hint.move;
    }
    const entry = tt.values.get(valueKey(key, depth));
    if (entry !== undefined
      && entry.occ === occ && entry.white === white && entry.rok === rok && entry.stm === stm) {
      const value = ttLoadValue(entry.value, ply);
      if (entry.flag === EXACT) return value;
      if (entry.flag === LOWER) {
        if (value > currentAlpha) currentAlpha = value;
      } else if (value < currentBeta) {
        currentBeta = value;
      }
      if (currentAlpha >= currentBeta) return value;
    }
  }

  const moves = genMoves(occ, white, rok, stm);
  if (moves.length === 0) {
    // The side to move cannot move and has therefore lost (§7.1). Scaling by
    // ply prefers the fastest win and the longest resistance.
    return -(WINNING_SCORE - ply);
  }
  const black = occ & ~white;
  if (!black || !white) {
    const loser = !black ? BLACK : WHITE;
    return loser === stm ? -(WINNING_SCORE - ply) : WINNING_SCORE - ply;
  }

  if (depth <= 0) {
    const score = evaluate(occ, white, rok);
    return stm === WHITE ? score : -score;
  }

  if (moves.length > 1) orderMoves(moves, occ, white, rok, stm, ttMove);

  let bestScore = -Infinity;
  let bestMove = -1;
  for (let i = 0; i < moves.length; i += 1) {
    const move = moves[i];
    makeMove(occ, white, rok, stm, move);
    const cOcc = MM_OCC;
    const cWhite = MM_WHITE;
    const cRok = MM_ROK;
    const cStm = MM_STM;
    const score = -search(cOcc, cWhite, cRok, cStm,
      depth - 1, -currentBeta, -currentAlpha, ply + 1, tt, ctx);
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
    if (bestScore > currentAlpha) currentAlpha = bestScore;
    if (currentAlpha >= currentBeta) break;
  }

  if (tt !== null) {
    let flag;
    if (bestScore <= alphaOrig) flag = UPPER;
    else if (bestScore >= currentBeta) flag = LOWER;
    else flag = EXACT;
    tt.values.set(valueKey(key, depth), {
      value: ttStoreValue(bestScore, ply), flag, occ, white, rok, stm,
    });
    if (bestMove >= 0) tt.moves.set(key, { move: bestMove, occ, white, rok, stm });
  }

  return bestScore;
}

/**
 * Score every root move with a full window.
 *
 * Full windows cost pruning, but a narrowed window makes every sibling score
 * after the first a bound rather than a value — which would corrupt both the
 * best-move choice and the temperature sampling below.
 */
function rootScores(occ, white, rok, stm, depth, tt, ctx) {
  const moves = genMoves(occ, white, rok, stm);

  let hint = -1;
  if (tt !== null) {
    const entry = tt.moves.get(zobrist(occ, white, rok, stm));
    if (entry !== undefined
      && entry.occ === occ && entry.white === white && entry.rok === rok && entry.stm === stm) {
      hint = entry.move;
    }
  }
  orderMoves(moves, occ, white, rok, stm, hint);

  const scored = [];
  for (let i = 0; i < moves.length; i += 1) {
    const move = moves[i];
    makeMove(occ, white, rok, stm, move);
    const cOcc = MM_OCC;
    const cWhite = MM_WHITE;
    const cRok = MM_ROK;
    const cStm = MM_STM;
    scored.push([move, neg(search(cOcc, cWhite, cRok, cStm,
      depth - 1, -Infinity, Infinity, 1, tt, ctx))]);
  }

  scored.sort((a, b) => b[1] - a[1]);
  return scored;
}

/**
 * Softmax-sample among the top-k root moves.
 *
 * Scores are in centipieces and so is the temperature, so it behaves as an
 * actual exploration width: at 25, a move half a piece worse is chosen about
 * 14% as often as the best one.
 */
function sampleMove(scored, temperature, topK, random) {
  if (scored.length === 0) return null;
  const bestScore = scored[0][1];

  if (temperature && temperature > 0 && topK > 1) {
    const candidates = scored.slice(0, topK);
    const weights = candidates.map(([, s]) => Math.exp((s - bestScore) / temperature));
    const total = weights.reduce((sum, w) => sum + w, 0);
    if (total > 0 && Number.isFinite(total)) {
      let pick = random() * total;
      for (let i = 0; i < candidates.length; i += 1) {
        pick -= weights[i];
        if (pick <= 0) return candidates[i][0];
      }
      return candidates[candidates.length - 1][0];
    }
  }

  const best = scored.filter(([, s]) => s === bestScore).map(([m]) => m);
  return best[Math.floor(random() * best.length)];
}

/**
 * Iterative-deepening search entry point.
 *
 * @param {object} currentGameState  board + currentTurn
 * @param {number} depth             maximum search depth in plies
 * @param {*}      isMaximizingPlayer accepted for backwards compatibility and
 *   IGNORED. Min/max follows `currentTurn`, the only self-consistent reading;
 *   callers that passed a value inconsistent with the side to move previously
 *   got a search for the *opponent's* preference.
 * @param {object} options
 *   randomize      — sample among near-best moves instead of taking the best
 *   maxNodes       — node budget; returns the last completed depth
 *   maxMs          — wall-clock budget in milliseconds
 *   rootProbeNodes — deprecated, accepted and ignored (round-robin root
 *                    probing is gone; it was what made deep tiers no stronger
 *                    than shallow ones)
 *   stochasticTopK — how many root moves are sampling candidates
 *   temperature    — softmax width in centipieces; 0 disables sampling
 *   useTt          — enable the transposition table
 *   random         — () => [0,1) source, for reproducible tests
 *
 * @returns {object} `{ score, move, depth, scores }` — `score` is positive for
 *   WHITE, `move` is `{ from, to }` vertex names (null when there is no legal
 *   move), `depth` is the deepest completed iteration and `scores` lists every
 *   root move best-first as `[{ from, to }, score]` from the side-to-move's
 *   perspective.
 */
function getNextBestMove(currentGameState, depth = 8, isMaximizingPlayer = null, options = {}) {
  const {
    randomize = true,
    maxNodes = null,
    maxMs = null,
    stochasticTopK = 3,
    temperature = DEFAULT_TEMPERATURE,
    blunderRate = 0,
    useTt = true,
    random = Math.random,
  } = options;

  const [occ, white, rok, stm] = fromState(currentGameState);
  const moves = genMoves(occ, white, rok, stm);
  if (moves.length === 0) {
    return { score: 0, move: null, depth: 0, scores: [] };
  }

  const nodeBudget = maxNodes === null || maxNodes === undefined ? null : maxNodes;
  const msBudget = maxMs === null || maxMs === undefined ? null : maxMs;
  const ctx = nodeBudget === null && msBudget === null
    ? null
    : { nodes: 0, maxNodes: nodeBudget, deadline: msBudget === null ? null : now() + Math.max(msBudget, 0) };

  const tt = useTt ? new TranspositionTable() : null;
  let completed = null;
  let completedDepth = 0;

  for (let currentDepth = 1; currentDepth <= Math.max(1, depth); currentDepth += 1) {
    let scored;
    try {
      scored = rootScores(occ, white, rok, stm, currentDepth, tt, ctx);
    } catch (error) {
      if (error === SEARCH_ABORTED) break;
      throw error;
    }
    completed = scored;
    completedDepth = currentDepth;
    // A proven forced result cannot be improved by looking further.
    if (Math.abs(scored[0][1]) > MATE_THRESHOLD) break;
  }

  if (completed === null) {
    // Budget expired before even depth 1 finished — fall back to a static
    // ranking so a legal move is always returned.
    completed = moves.map((move) => {
      makeMove(occ, white, rok, stm, move);
      const score = evaluate(MM_OCC, MM_WHITE, MM_ROK);
      return [move, neg(MM_STM === WHITE ? score : -score)];
    }).sort((a, b) => b[1] - a[1]);
  }

  let chosen = randomize
    ? sampleMove(completed, temperature, stochasticTopK, random)
    : completed[0][0];

  // Deliberate mistake. Temperature alone cannot make the engine weak enough
  // for a beginner tier without also making it erratic near the top, so the
  // lowest calibrated tiers use an explicit rate of playing a demonstrably
  // worse move. strategy/engine/runner.py applies the identical rule (at the
  // runner level rather than here), which is what lets the measured Elo of a
  // tier carry over from the simulation ladder to the browser.
  if (randomize && blunderRate > 0 && completed.length > 1 && random() < blunderRate) {
    const alternatives = completed.slice(1, 3);
    chosen = alternatives[Math.floor(random() * alternatives.length)][0];
  }

  const stmScore = completed.find(([move]) => move === chosen)[1];

  return {
    score: stm === WHITE ? stmScore : neg(stmScore),
    move: moveToVertices(chosen),
    depth: completedDepth,
    scores: completed.map(([move, score]) => [moveToVertices(move), score]),
  };
}

// -- Rules API (dict game-state) -------------------------------------------

/** Move validation — implements patent §3. */
const isValidMove = (gameState, from, to) => {
  if (from === to) return false;
  if (!EDGE_SET.has(`${from}|${to}`)) return false;

  const checker = gameState.checkers[from];
  if (!checker) return false;
  if (gameState.checkers[to]) return false;
  if (checker.color !== gameState.currentTurn) return false;

  // Roks move in any direction (§3.3); so do cobs on their own home base (§3.2)
  if (checker.isUpgraded || HOME_BASES[checker.color].includes(from)) return true;

  // Forward-only for regular cobs (§3.1)
  const colour = checker.color === 'WHITE' ? WHITE : BLACK;
  return !!(FWD[colour][INDEX[from]] & (1 << INDEX[to]));
};

/**
 * Return [{ from, to }, ...] for the current player.
 *
 * When no normal move exists this returns the dead-piece promotions of patent
 * §6.3, represented as { from: v, to: v, isPromotion: true }.
 */
const getAllPossibleMoves = (gameState) => genMoves(...fromState(gameState)).map((move) => {
  const { from, to } = moveToVertices(move);
  return from === to ? { from, to, isPromotion: true } : { from, to };
});

/**
 * Full legality test — the predicate the UI should use.
 *
 * `isValidMove` above implements §3 only, and §3 has no concept of a move that
 * starts and ends on the same vertex, so it rejects the §6.3 dead-piece
 * promotion by construction. That promotion is legal precisely when no §3 move
 * exists, which can only be answered by generating the whole move set. The
 * server's facade (api/src/lib/engine.ts) resolves it the same way, so both
 * sides agree by construction.
 */
const isLegalMove = (gameState, from, to) =>
  getAllPossibleMoves(gameState).some((m) => m.from === from && m.to === to);

/**
 * Vertices whose piece can be promoted in place (§6.3). Non-empty only when the
 * side to move has no ordinary move, so the UI can surface it as the forced
 * alternative rather than leaving the player stuck.
 */
const getPromotionMoves = (gameState) =>
  getAllPossibleMoves(gameState)
    .filter((m) => m.from === m.to)
    .map((m) => m.from);

/** Apply move and toggle turn. */
const ApplyMoveAI = (boardState, from, to) => {
  const newState = applyMoveToBoard(boardState, from, to);
  return { ...newState, currentTurn: OPPONENT[boardState.currentTurn] };
};

/** Game ends when the player to move has no moves, or one colour is gone. */
const isGameOver = (gameState) => terminalLoser(...fromState(gameState)) !== -1;

/**
 * Static evaluation in centipieces — positive favours WHITE.
 *
 * Every term is built either on RANK (antisymmetric under the board's 180°
 * automorphism) or on vertex sets that map onto their opposite under it, so
 * eval(P) === -eval(rotateAndSwap(P)) holds exactly.
 */
const evaluateBoard = (gameState) => {
  const [occ, white, rok] = fromState(gameState);
  return evaluate(occ, white, rok);
};

// -- Draw detection utilities (patent §7.2) --------------------------------

/** Deterministic position hash for repetition tracking. */
const hashPosition = (gameState) => zobrist(...fromState(gameState));

/** Threefold repetition: true if any position has appeared >= 3 times. */
const checkThreefoldRepetition = (positionHashes) => {
  const counts = new Map();
  for (const h of positionHashes) {
    const next = (counts.get(h) || 0) + 1;
    if (next >= 3) return true;
    counts.set(h, next);
  }
  return false;
};

/**
 * 50-move rule: true if the last 100 half-moves had no cob movement or
 * promotion. `cobMovedFlags` is a boolean[] parallel to the move history.
 */
const checkFiftyMoveRule = (cobMovedFlags) => {
  if (cobMovedFlags.length < 100) return false;
  return cobMovedFlags.slice(-100).every((flag) => !flag);
};

const AI = {
  getNextBestMove,
  isGameOver,
  evaluateBoard,
  getAllPossibleMoves,
  ApplyMoveAI,
  isValidMove,
  isLegalMove,
  getPromotionMoves,
  hashPosition,
  checkThreefoldRepetition,
  checkFiftyMoveRule,
};

export default AI;

// Internals exported for the parity and search tests only — not part of the
// app-facing API above. (EVAL_WEIGHTS is exported at its definition.)
export {
  WHITE, BLACK, VERTICES, RANK, MAX_RANK, FORWARD, DEAD_POSITIONS,
  fromState, toState, genMoves, evaluate, zobrist, setWeights,
  DEFAULT_TEMPERATURE,
};
